(ns ui.editors.entity-creator.event
  (:require [re-frame.core :as rf]
            [common.localization :as loc]
            [common.util :as cu]
            [common.schema.entities.pipeline :as pipeline]
            [ui.editors.entity-creator.model :as m]
            [frontend.controller.websocket.event :as ws]
            [frontend.view.modals.events :as modals]
            [frontend.view.modals.error-message.view :as error-message]
            [ui.cofx.util :as cofx]
            [ui.fx.toast :as toast]
            [potpuri.core :as potpuri]
            [frontend.state.current-session :as session]
            [common.schema.entity-creator.utils :as ecu]
            [common.schema.entry-criteria.model :as criteria-model]))

(defn field-schemas-cofx
  [{:keys [db event] :as cofx}]
  (let [[_ {:keys [entity-type pipeline]}] event
        ;; pipeline (if applicable) as event arg in the
        ;; open-config-event; inside state otherwise:
        pipeline (or (get-in db m/pipeline-full-path)
                     pipeline)
        field-configs (:tenant/field-configurations @session/tenant)
        ctx {:pipeline pipeline
             :tenant @session/tenant
             :tenant-schema @session/tenant-schema
             :entity-type entity-type
             :field-configs field-configs}
        field-schemas (ecu/configurable-creator-fields ctx entity-type)]
    (assoc cofx
           :field-schemas field-schemas)))

(rf/reg-cofx m/field-schemas-cofx field-schemas-cofx)

(defn system-mandatory-cofx
  [{:keys [db event] :as cofx}]
  (let [[_ {:keys [entity-type target-type pipeline]}] event
        ;; pipeline (if applicable) as event arg in the
        ;; open-config-event; inside state otherwise:
        pipeline (or (get-in db m/pipeline-full-path)
                     pipeline)
        field-configs (:tenant/field-configurations @session/tenant)
        ctx {:pipeline pipeline
             :tenant @session/tenant
             :tenant-schema @session/tenant-schema
             :field-configs field-configs}
        mandatory (-> (ecu/mandatory-fields ctx entity-type)
                      keys
                      set)
        target-mandatory (when target-type
                           (-> (ecu/mandatory-fields ctx target-type)
                               keys
                               set))
        target-profile-type (ecu/resolve-target-profile-type
                             {:pipeline pipeline
                              :entity-type entity-type
                              :target-type target-type})
        target-profile-mandatory (when target-profile-type
                                   (-> (ecu/mandatory-fields ctx target-profile-type)
                                       keys
                                       set))]
    (cond-> cofx
      true (assoc :system-mandatory mandatory)
      target-type (assoc :target-system-mandatory target-mandatory)
      target-profile-type (assoc :target-profile-system-mandatory
                                 target-profile-mandatory))))

(rf/reg-cofx m/system-mandatory-cofx system-mandatory-cofx)

(defn stages-cofx
  [{:keys [db event] :as cofx}]
  (if-let [pipeline (or (get-in db m/pipeline-full-path)
                        (some-> event
                                (get 1)
                                :pipeline))]
    (let [pipeline-id (:db/id pipeline)
          pipeline-entity-type (:pipeline/entity-type pipeline)
          stage-pipeline-attribute (get pipeline/entity->stage-pipeline-ref
                                        pipeline-entity-type)
          stage-entity-type (get pipeline/entity-type-to-stage-mappings pipeline-entity-type)
          stages (->> (session/get-entities stage-entity-type)
                      (cu/filter-by stage-pipeline-attribute pipeline-id)
                      (sort-by :common/ord))]
      (assoc cofx :stages stages))
    cofx))

(defn tenant-cofx
  [cofx]
  (assoc cofx :tenant @session/tenant))

(rf/reg-cofx m/stages-cofx stages-cofx)
(rf/reg-cofx m/tenant-cofx tenant-cofx)

(defn resolve-target-profile-config
  [{:keys [config-path raw-config system-mandatory config-fn config-mandatory-fn]}]
  (let [target-profile-config (when config-path
                                (get-in raw-config config-path))
        ;;TODO currently the only one - update if changes:
        target-profile-type :entity.type/investor
        target-profile-config (or (not-empty target-profile-config)
                                  (ecu/default-config
                                   {:config-fn config-fn
                                    :entity-type target-profile-type}))
        target-profile-config-attributes (->> target-profile-config
                                              :fields
                                              (mapv :attribute)
                                              set)
        missing-mandatory (->> system-mandatory
                               (remove (fn [a]
                                         (contains? target-profile-config-attributes a)))
                               (mapv config-mandatory-fn))
        target-profile-config (if (seq missing-mandatory)
                                (update target-profile-config :fields
                                        (fn [fields]
                                          (into missing-mandatory fields)))
                                target-profile-config)
        static-target-set (get-in ecu/static-fields [target-profile-type :set])
        ;; removing static fields from list of configured fields
        ;; (will be injected during creation)
        target-profile-config (if (seq static-target-set)
                                (update target-profile-config :fields
                                        (fn [fields]
                                          (->> fields
                                               (filterv (fn [{:keys [attribute]}]
                                                          (not (contains? static-target-set
                                                                          attribute)))))))
                                target-profile-config)]
    target-profile-config))

(rf/reg-event-fx
 m/open-config
 [(rf/inject-cofx m/tenant-cofx)
  (rf/inject-cofx m/stages-cofx)
  (rf/inject-cofx m/system-mandatory-cofx)]
 (fn [{:keys [db stages tenant system-mandatory target-system-mandatory
              target-profile-system-mandatory]}
      [_ {:keys [entity-type target-type pipeline] :as _args}]]
   (let [raw-config (if pipeline
                      (:pipeline/creator-config pipeline)
                      (:tenant/entity-creation-config tenant))
         {:keys [main-config-path
                 target-config-path
                 target-profile-config-path
                 associations-config-path]} (ecu/resolve-config-paths
                                             {:entity-type entity-type
                                              :target-type target-type
                                              :pipeline pipeline})
         entity-config-path main-config-path
         entity-config (get-in raw-config entity-config-path)
         config-fn (if (seq stages)
                     (fn [attribute setting]
                       {:attribute attribute
                        :stages
                        (->> stages
                             (mapv (fn [{:keys [db/id]}]
                                     [id {:setting
                                          setting}]))
                             (into {}))})
                     (fn [attribute setting]
                       {:attribute attribute
                        :create {:setting setting}}))
         entity-config (or entity-config
                           (ecu/default-config
                            {:config-fn config-fn
                             :entity-type entity-type}))
         config-attributes (->> entity-config
                                :fields
                                (mapv :attribute)
                                set)
         config-mandatory-fn #(config-fn % :mandatory)
         ;;no-pipeline-mandatory {:create {:setting :mandatory}}
         missing-mandatory (->> system-mandatory
                                (remove (fn [a]
                                          (contains? config-attributes a)))
                                (mapv config-mandatory-fn))

         entity-config (if (seq missing-mandatory)
                         (update entity-config :fields
                                 (fn [fields]
                                   (into missing-mandatory fields)))
                         entity-config)
         entity-config (ecu/ensure-protected-fields
                        {:config entity-config
                         :config-fn config-fn
                         :entity-type entity-type})
         target-config (when target-config-path
                         (get-in raw-config target-config-path))
         target-config (or target-config
                           (ecu/default-config
                            {:config-fn config-fn
                             :entity-type target-type}))
         target-config-attributes (->> target-config
                                       :fields
                                       (mapv :attribute)
                                       set)
         missing-target-mandatory (->> target-system-mandatory
                                       (remove (fn [a]
                                                 (contains? target-config-attributes a)))
                                       (mapv config-mandatory-fn))
         target-config (if (seq missing-target-mandatory)
                         (update target-config :fields
                                 (fn [fields]
                                   (into missing-target-mandatory fields)))
                         target-config)
         target-config (ecu/ensure-protected-fields
                        {:config target-config
                         :config-fn config-fn
                         :entity-type target-type})
         ;; explicit investor-profile fields for froppo/sales-oppo:
         target-profile-config (when (ecu/resolve-target-profile-type
                                      {:pipeline pipeline
                                       :entity-type entity-type
                                       :target-type target-type})
                                 (resolve-target-profile-config
                                  {:raw-config raw-config
                                   :config-path target-profile-config-path
                                   :system-mandatory target-profile-system-mandatory
                                   :config-fn config-fn
                                   :config-mandatory-fn config-mandatory-fn}))
         associations-config (get-in raw-config associations-config-path)
         edit-state (cond-> {}
                      true (assoc-in entity-config-path entity-config)
                      target-config-path (assoc-in target-config-path target-config)
                      target-profile-config-path (assoc-in target-profile-config-path
                                                           target-profile-config)
                      (seq associations-config) (assoc-in associations-config-path
                                                          associations-config))
         initial-state {m/open-key true
                        ;;TODO both initial config and edit-config needed?
                        ;;     what if updated (and synced) during edit?
                        m/entity-type-key entity-type
                        m/target-type-key target-type
                        m/pipeline-key pipeline
                        m/edits-key edit-state}]
     {:db (-> db
              (assoc m/root initial-state))})))

(rf/reg-event-db
 m/close-config
 (fn [db _]
   (dissoc db m/root)))

(defn add-attribute
  [{:keys [db stages]} [_ {:keys [config-path attribute stages-config create-config]}]]
  (let [edits (get-in db m/edits-full-path)
        config (get-in edits config-path)
        stages? (seq stages)
        selected-fields (->> config
                             :fields
                             (mapv :attribute)
                             (into #{}))
        default-config {:setting (or create-config :optional)}
        stages-config (->> stages
                           (mapv (fn [{:keys [db/id]}]
                                   (let [setting (get stages-config id :optional)]
                                     [id {:setting setting}])))
                           (into {}))
        attribute-config (cond-> {:attribute attribute}
                           stages? (assoc :stages stages-config)
                           (not stages?) (assoc :create default-config))
        new-config (if-not (contains? selected-fields attribute)
                     (update config :fields (fnil conj []) attribute-config)
                     config)
        new-edits (assoc-in edits config-path new-config)]
    {:db (-> db
             (assoc-in m/edits-full-path new-edits))}))

(defn filter-excluded-fields
  [config exclude-attributes]
  (if (seq exclude-attributes)
    (update config :fields
            (fn [fields]
              (->> fields
                   (filterv (fn [{:keys [attribute]}]
                              (not (contains? exclude-attributes attribute)))))))
    config))

(defn reorder-fields
  [{:keys [db]} [_ {:keys [config-path entity-type from to exclude-attributes]}]]
  (let [edits (get-in db m/edits-full-path)
        config (-> (get-in edits config-path)
                   (filter-excluded-fields exclude-attributes))
        fields (:fields config)
        new-fields (ecu/protected-fields-first
                    {:entity-type entity-type
                     :fields (cu/vec-move fields from to)})
        new-config (assoc config
                          :fields
                          new-fields)
        new-edits (assoc-in edits config-path new-config)]
    {:db (-> db
             (assoc-in m/edits-full-path new-edits))}))

(defn reorder-associations
  [{:keys [db]} [_ {:keys [config-path from to]}]]
  (let [edits (get-in db m/edits-full-path)
        config (get-in edits config-path)
        new-config (update config
                           :associations
                           cu/vec-move from to)
        new-edits (assoc-in edits config-path new-config)]
    {:db (-> db
             (assoc-in m/edits-full-path new-edits))}))

(defn set-entry-criteria-mode
  "Sets when the entry-criteria modal opens for the config at `config-path`
   (the main config of a pipeline). The default mode removes the key instead of
   storing it, so a config that never had a mode is unchanged after the user
   picks the default again and `m/changes?` stays false."
  [{:keys [db]} [_ {:keys [config-path mode]}]]
  (let [edits (get-in db m/edits-full-path)
        config (get-in edits config-path)
        new-config (if (= mode criteria-model/default-entry-criteria-mode)
                     (dissoc config :entry-criteria-mode)
                     (assoc config :entry-criteria-mode mode))
        new-edits (assoc-in edits config-path new-config)]
    {:db (assoc-in db m/edits-full-path new-edits)}))

(rf/reg-event-fx
 m/set-entry-criteria-mode
 []
 #'set-entry-criteria-mode)

(defn remove-attribute
  [{:keys [db system-mandatory]}
   [_ {:keys [config-path entity-type index attribute exclude-attributes]}]]
  (when-not (or (contains? system-mandatory attribute)
                (ecu/protected-creator-field? entity-type attribute))
    (let [edits (get-in db m/edits-full-path)
          config (-> (get-in edits config-path)
                     (filter-excluded-fields exclude-attributes))
          new-config (update config :fields (partial cu/vec-remove index))
          new-edits (assoc-in edits config-path new-config)]
      {:db (-> db
               (assoc-in m/edits-full-path new-edits))})))

(defn update-field-config
  [{:keys [db _system-mandatory]}
   [_ {:keys [index config-path stage-id
              _field-config stage-config
              _create-config exclude-attributes
              setting]}]]
  (when-not (get stage-config :non-editable?)
    (let [edits (get-in db m/edits-full-path)
          config (-> (get-in edits config-path)
                     (filter-excluded-fields exclude-attributes))
          path (if stage-id
                 [:fields index :stages stage-id :setting]
                 [:fields index :create :setting])
          new-config (assoc-in config path setting)
          new-edits (assoc-in edits config-path new-config)]
      {:db (-> db
               (assoc-in m/edits-full-path new-edits))})))

(defn edit-default-value
  [{:keys [db system-mandatory]}
   [_ {:keys [index config-path _entity-type attribute exclude-attributes]}]]
  ;;when allowed?
  (when-not (contains? system-mandatory attribute)
    (let [edits (get-in db m/edits-full-path)
          config (-> (get-in edits config-path)
                     (filter-excluded-fields exclude-attributes))
          ;;TODO only if not non-editable
          new-config (assoc-in config [:fields index :edit-default?] true)
          new-edits (assoc-in edits config-path new-config)]
      {:db (-> db
               (assoc-in m/edits-full-path new-edits))})))

(defn cleanup-field-config
  [field-cfg]
  (select-keys field-cfg
               [:attribute
                :stages
                :create]))

(defn cleanup-config
  "Keeps what a config is saved with. `:entry-criteria-mode` only exists in
   the main config of a pipeline, and the other configs never carry it. A nil
   mode is the same as none, so it is not saved."
  [edit-config]
  (-> edit-config
      (select-keys [:fields :entry-criteria-mode])
      cu/remove-nil-vals
      (update :fields
              (fn [fields]
                (->> fields
                     (mapv cleanup-field-config))))))

(defn save-config
  [{:keys [db _system-mandatory tenant]}
   _]
  (let [edits (get-in db m/edits-full-path)
        entity-type (get-in db m/entity-type-full-path)
        target-type (get-in db m/target-type-full-path)
        pipeline (get-in db m/pipeline-full-path)
        {:keys [main-config-path
                target-config-path
                target-profile-config-path
                associations-config-path]} (ecu/resolve-config-paths
                                            {:entity-type entity-type
                                             :target-type target-type
                                             :pipeline pipeline})
        entity-config-path main-config-path
        entity-edits (get-in edits entity-config-path)
        target-edits (get-in edits target-config-path)
        target-profile-edits (get-in edits target-profile-config-path)
        association-edits (get-in edits associations-config-path)
        pipeline-id (:db/id pipeline)
        entity-config (cleanup-config entity-edits)
        target-config (cleanup-config target-edits)
        target-profile-config (cleanup-config target-profile-edits)
        current-config (if pipeline
                         (:pipeline/creator-config pipeline)
                         (:tenant/entity-creation-config tenant))
        new-config (cond-> current-config
                     true (assoc-in entity-config-path entity-config)
                     true (assoc-in associations-config-path association-edits)
                     target-config-path (assoc-in target-config-path target-config)
                     target-profile-config-path (assoc-in target-profile-config-path
                                                          target-profile-config))
        ;; note: first release: only single (asset) entities => saving only
        ;; to :tenant/entity-creation-config and target-config will be nil
        handler-id (if pipeline
                     :entity/update-attribute
                     :tenant/update)
        body (if pipeline
               {:db/id pipeline-id
                :attribute :pipeline/creator-config
                :value new-config}
               {:db/id (:db/id tenant)
                :tenant/entity-creation-config (pr-str new-config)})]
    {:db (-> db
             (assoc-in m/saving-full-path true))
     :fx [[::ws/send
           {:id handler-id
            :body body
            :on-success [m/save-config-success]
            :on-error [m/save-config-error]}]]}))

(rf/reg-event-fx
 m/add-attribute
 [(rf/inject-cofx m/system-mandatory-cofx)
  (rf/inject-cofx m/stages-cofx)]
 #'add-attribute)

(rf/reg-event-fx
 m/remove-attribute
 [(rf/inject-cofx m/system-mandatory-cofx)]
 #'remove-attribute)

(defn remove-association
  [{:keys [db system-mandatory]} [_ {:keys [config-path index association]}]]
  (when-not (contains? system-mandatory association)
    (let [edits (get-in db m/edits-full-path)
          config (get-in edits config-path)
          new-config (update config :associations (partial cu/vec-remove index))
          new-edits (assoc-in edits config-path new-config)]
      {:db (-> db
               (assoc-in m/edits-full-path new-edits))})))

(rf/reg-event-fx
 m/remove-association
 [(rf/inject-cofx m/system-mandatory-cofx)]
 #'remove-association)

(defn add-association
  [{:keys [db stages]} [_ {:keys [config-path association stages-config
                                  create-setting prefill]}]]
  (let [edits (get-in db m/edits-full-path)
        config (get-in edits config-path)
        stages? (seq stages)
        selected-associations (->> config
                                   :associations
                                   (mapv :association-key)
                                   (into #{}))
        stages-config (->> stages
                           (mapv (fn [{:keys [db/id]}]
                                   (let [setting (get stages-config id :optional)]
                                     [id {:setting setting}])))
                           (into {}))
        create-setting (or create-setting :optional)
        associations-config (cond-> {:association-key association}
                              stages? (assoc :stages stages-config)
                              (not stages?) (assoc :create {:setting create-setting})
                              prefill (assoc :prefill-from prefill))
        new-config (if-not (contains? selected-associations association)
                     (update config :associations (fnil conj []) associations-config)
                     config)
        new-edits (assoc-in edits config-path new-config)]
    {:db (-> db
             (assoc-in m/edits-full-path new-edits))}))

(rf/reg-event-fx
 m/add-association
 [(rf/inject-cofx m/system-mandatory-cofx)
  (rf/inject-cofx m/stages-cofx)]
 #'add-association)

(defn replace-config-at-path
  [{:keys [db]} [_ {:keys [config-path new-config]}]]
  (let [edits (get-in db m/edits-full-path)
        new-edits (assoc-in edits config-path new-config)]
    {:db (assoc-in db m/edits-full-path new-edits)
     ::toast/success (loc/tr :creator-config/copy-toast)}))

(rf/reg-event-fx
 m/replace-config-at-path
 #'replace-config-at-path)

(rf/reg-event-fx
 m/reorder-fields
 #'reorder-fields)

(rf/reg-event-fx
 m/reorder-associations
 #'reorder-associations)

(rf/reg-event-fx
 m/update-field-config
 #'update-field-config)

(defn update-association-prefill
  [{:keys [db]} [_ {:keys [index config-path prefill]}]]
  (let [edits (get-in db m/edits-full-path)
        config (get-in edits config-path)
        path [:associations index :prefill-from]
        new-config (if prefill
                     (assoc-in config path prefill)
                     (potpuri/dissoc-in config path))
        new-edits (assoc-in edits config-path new-config)]
    {:db (-> db
             (assoc-in m/edits-full-path new-edits))}))

(rf/reg-event-fx
 m/update-association-prefill
 #'update-association-prefill)

(defn enrich-visibility
  [{:keys [stages]} visibility]
  (if (seq stages)
    (->> visibility
         (mapv (fn [[stage-id visibility-setting]]
                 [stage-id {:setting visibility-setting}]))
         (into {}))
    ;;TODO ensure visibility for :create is only keyword
    {:setting visibility}))

(defn update-association-visibility
  [{:keys [db stages]} [_ {:keys [index config-path visibility]}]]
  (let [edits (get-in db m/edits-full-path)
        config (get-in edits config-path)
        visibility-key (if (seq stages)
                         :stages
                         :create)
        path [:associations index visibility-key]
        visibility-config (enrich-visibility {:stages stages}
                                             (get visibility visibility-key))
        ;;TODO later: deep merge or similar needed if contains more settings beside
        ;; {:setting :optional/:mandatory}
        new-config (assoc-in config path visibility-config)
        new-edits (assoc-in edits config-path new-config)]
    {:db (-> db
             (assoc-in m/edits-full-path new-edits))}))

(rf/reg-event-fx
 m/update-association-visibility
 [(rf/inject-cofx m/stages-cofx)]
 #'update-association-visibility)

(defn update-field-visibility
  [{:keys [db stages]}
   [_ {:keys [index config-path entity-type attribute visibility exclude-attributes]}]]
  (let [visibility-key (if (seq stages)
                         :stages
                         :create)
        setting (get visibility visibility-key)
        settings (if (map? setting)
                   (vals setting)
                   [setting])]
    (when-not (and (ecu/protected-creator-field? entity-type attribute)
                   (some #{:not-shown} settings))
      (let [edits (get-in db m/edits-full-path)
            config (-> (get-in edits config-path)
                       (filter-excluded-fields exclude-attributes))
            path [:fields index visibility-key]
            visibility-config (enrich-visibility {:stages stages}
                                                 setting)
            ;;TODO later: deep merge or similar needed if contains more settings beside
            ;; {:setting :optional/:mandatory}
            new-config (assoc-in config path visibility-config)
            new-edits (assoc-in edits config-path new-config)]
        {:db (-> db
                 (assoc-in m/edits-full-path new-edits))}))))

(rf/reg-event-fx
 m/update-field-visibility
 [(rf/inject-cofx m/stages-cofx)]
 #'update-field-visibility)

(rf/reg-event-fx
 m/edit-default-value
 [#_(rf/inject-cofx m/field-schemas-cofx)
  (rf/inject-cofx m/system-mandatory-cofx)]
 #'edit-default-value)

(rf/reg-event-fx
 m/save-config
 [(rf/inject-cofx cofx/tenant-cofx)
  (rf/inject-cofx m/system-mandatory-cofx)]
 #'save-config)

(defn save-config-success
  [{:keys [_db]} [_ _success-response]]
  {:fx [[:dispatch [m/close-config]]]})

(rf/reg-event-fx
 m/save-config-success
 []
 #'save-config-success)

(defn open-error-modal!
  [error-message]
  (modals/open-modal!
   error-message/modal-key
   {:title
    (loc/tr :attention)
    :body-content
    error-message
    :ok-button-text
    (loc/tr :ok)}))

(defn save-config-error
  [{:keys [db _system-mandatory]}
   [_ error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (-> db
                   (potpuri/dissoc-in m/saving-full-path))]
    {:db new-db
     :fx [[::modals/open-modal
           (partial open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/save-config-error
 []
 #'save-config-error)

(defn toggle-prevent-create
  "Lets the field-requirements table header (fields-table-header in
   ui.editors.entity-creator.view) author the same :prevent-create? stage
   lock the pipeline-stages curtain does, without leaving this modal.
   Optimistically updates this modal's own pipeline snapshot (m/pipeline,
   a one-time copy taken at open-config time - see that event's docstring)
   so the lock icon flips immediately; the real-time entity sync worker
   picks up the persisted change for every other surface (curtain, kanban
   '+' button, navbar create dropdown) the same way it always has."
  [{:keys [db]} [_ {:keys [stage-id]}]]
  (let [pipeline (get-in db m/pipeline-full-path)
        pipeline-id (:db/id pipeline)
        old-config (:pipeline/creator-config pipeline)
        new-config (ecu/toggle-prevent-create-config old-config stage-id)]
    (if (= old-config new-config)
      {}
      {:db (assoc-in db (conj m/pipeline-full-path :pipeline/creator-config) new-config)
       :fx [[::ws/send
             {:id :entity/update-attribute
              :body {:db/id pipeline-id
                     :attribute :pipeline/creator-config
                     :value new-config}
              :on-success [m/toggle-prevent-create-success]
              :on-error [m/toggle-prevent-create-error old-config]}]]})))

(rf/reg-event-fx
 m/toggle-prevent-create
 []
 #'toggle-prevent-create)

(rf/reg-event-fx
 m/toggle-prevent-create-success
 []
 (fn [_ _] {}))

(defn toggle-prevent-create-error
  [{:keys [db]} [_ old-config error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))]
    {:db (assoc-in db (conj m/pipeline-full-path :pipeline/creator-config) old-config)
     :fx [[::modals/open-modal
           (partial open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/toggle-prevent-create-error
 []
 #'toggle-prevent-create-error)
